/**
 * ADMIN PAGE-VIEW INSTRUMENTATION - the client half
 * (spec 2026-08-20-admin-agent-catalogue-design.md 13.6).
 *
 * ===========================================================================
 * A ROUTE PATTERN, NEVER A RAW PATH
 * ===========================================================================
 *
 * `location.pathname` is matched against the frozen table below and the
 * MATCHED PATTERN is what leaves the browser. `/users/3fa85f64-...` emits
 * `/users/:id`. Anything the table does not cover is sent as the literal
 * `'unmatched'` - never as the pathname that failed to match.
 *
 * That is the whole privacy argument, and it is structural rather than
 * procedural: a member identifier cannot enter telemetry BY CONSTRUCTION.
 * Redaction would be a control somebody has to remember on each of 79 pages.
 *
 * The backend re-checks membership anyway (`utils/admin-telemetry.ts`),
 * because a browser is not a control.
 *
 * ===========================================================================
 * ONE MOUNT, IN AdminLayout
 * ===========================================================================
 *
 * `useAdminPageView()` is called ONCE, in `components/layout/AdminLayout.tsx`,
 * which wraps every authenticated route. Calling it per page would be 79
 * chances to forget, and the pages that got forgotten would read as unused -
 * an instrument whose silence is indistinguishable from its signal.
 *
 * ===========================================================================
 * IT CAN NEVER BREAK A PAGE
 * ===========================================================================
 *
 * The beacon is fire-and-forget with a swallowed rejection. An admin page must
 * render when analytics is down.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adminFetch } from '../providers/admin-api';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * THE FROZEN ROUTE-PATTERN TABLE - the client half of a two-copy pair.
 *
 * Derived from `App.tsx`. The backend holds a byte-identical copy in
 * `backend/src/utils/admin-telemetry.ts`, because the two artifacts are built
 * by different toolchains and neither can import the other.
 * `tests/unit/lint/admin-telemetry-route-patterns.test.ts` pins the pair to
 * EXACT equality and pins both against `App.tsx`, so a new page cannot
 * silently become `'unmatched'` and the mirror cannot drift.
 *
 * ORDER IS LOAD-BEARING: `matchRoutePattern` returns the FIRST match, and a
 * pattern segment beginning with `:` matches any one segment. `/matches/new`
 * must therefore precede `/matches/:matchId`, or a real page reports itself as
 * a detail view.
 */
export const ADMIN_ROUTE_PATTERNS: readonly string[] = Object.freeze([
  '/login',
  '/dashboard',
  '/launch-command-center',
  '/growth',
  '/users',
  '/users/:id',
  '/waitlist',
  '/beta-applications',
  '/support',
  '/feedback',
  '/verification',
  '/verification/whitelist',
  '/moderation',
  '/reports',
  '/audit',
  '/settings',
  '/security',
  '/security/admins',
  '/security/roles',
  '/security/permissions',
  '/promotions',
  '/promotions/new',
  '/promotions/:id',
  '/promotions/:id/edit',
  '/referrals',
  '/referrals/new',
  '/referrals/:id',
  '/questions',
  '/questions/new',
  '/questions/:id/edit',
  '/coupons/bulk-grant',
  '/segments',
  '/acquisition',
  '/system-health',
  '/notifications',
  '/matches',
  '/matches/compare',
  '/matches/new',
  '/matches/:matchId',
  '/matches/:matchId/scoring-diagnostic',
  '/campaign-links',
  // ── Declared AHEAD of the pages, deliberately (spec 16.5's rule, applied) ──
  // Build step 17 adds these two routes to App.tsx. A pattern table that
  // learns about a route only when somebody remembers to extend it is the
  // regression class this repository keeps shipping: until the entry exists,
  // every visit to the agent lands in `'unmatched'` and the ONE question this
  // instrument was built to answer - which pages are used - is unanswerable
  // for exactly the surface the project is about.
  '/agent',
  '/agent/a/:token',
  // Support inbox (design 2026-09-26, section 6). Declared with the stage C
  // backend, ahead of the pages, for the same reason the agent's were; the
  // pages landed in stage D (App.tsx). Specific before param.
  '/support-inbox',
  '/support-inbox/:threadId',
  // App Experience (spec 2026-10-23 section 5). Declared with the backend
  // (Task 7), ahead of the page, for the same reason as the two above; the
  // page lands in App.tsx with the admin UI (Task 10).
  '/app-experience',
]);

/** The literal sent when no pattern matched. Never the raw pathname. */
export const UNMATCHED_ROUTE_PATTERN = 'unmatched';

function segmentsOf(value: string): string[] {
  return value.split('/').filter((s) => s.length > 0);
}

/**
 * Resolve a pathname to a route pattern.
 *
 * A `:param` segment matches exactly one non-empty segment; every other
 * segment must match literally. First match wins, so the table's order is part
 * of the contract (see the table's own note).
 *
 * Exported for the lint: a matcher whose negative was never observed proves
 * nothing about the table it reads.
 */
export function matchRoutePattern(pathname: string): {
  pattern: string;
  matched: boolean;
} {
  const parts = segmentsOf(typeof pathname === 'string' ? pathname : '');
  for (const pattern of ADMIN_ROUTE_PATTERNS) {
    const shape = segmentsOf(pattern);
    if (shape.length !== parts.length) continue;
    let ok = true;
    for (let i = 0; i < shape.length; i++) {
      if (shape[i].startsWith(':')) continue;
      if (shape[i] !== parts[i]) {
        ok = false;
        break;
      }
    }
    if (ok) return { pattern, matched: true };
  }
  return { pattern: UNMATCHED_ROUTE_PATTERN, matched: false };
}

/**
 * Mount ONCE, in AdminLayout. Fires on every `location.pathname` change.
 *
 * The `lastSent` ref suppresses the duplicate a StrictMode double-mount would
 * otherwise produce, so "opens per admin" counts navigations rather than
 * React lifecycle events.
 *
 * 🔴 `enabled` IS NOT A CONVENIENCE - IT IS WHAT SAVES THE FIRST PAGE VIEW OF
 * EVERY SESSION. React forbids a conditional hook, so AdminLayout must call
 * this before its `isLoading` early return. On that first render there is no
 * Cognito session yet, `adminFetch` fails closed with a 401 BEFORE any network
 * call, the rejection is swallowed - and `lastSent` would have already been
 * armed with the pattern, so the retry after authentication resolves would be
 * suppressed and the landing page would look unvisited forever. Gating on
 * `enabled` keeps the ref untouched until the session exists, and `enabled` is
 * a dependency, so the effect re-runs the moment it flips.
 */
export function useAdminPageView(enabled = true): void {
  const location = useLocation();
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const { pattern } = matchRoutePattern(location.pathname);
    if (lastSent.current === pattern) return;
    lastSent.current = pattern;

    // Fire-and-forget. An admin page must render when analytics is down.
    void adminFetch(`${API_URL}/v1/admin/telemetry/page-view`, {
      method: 'POST',
      body: JSON.stringify({ route_pattern: pattern }),
    }).catch(() => {
      /* swallow - telemetry must never surface as an error to an operator */
    });
  }, [location.pathname, enabled]);
}
